import { RootState } from "@/redux/store";
import { useGetPropertiesByCommunity } from "@/services";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { CLOSING_CONTACT_EMAIL, CLOSING_CONTACT_NAME, CLOSING_CONTACT_PHONE, CLOSING_CONTACT_SOURCE, COMMUNITY, PROPERTY_ID, REPORTER_CONTACT, REPORTER_EMAIL, REPORTER_NAME, REPORTER_PHONE,
  RECEIPT_DATE,
  RECEIPT_FROM,
  REPORTER_TITLE,
  SELLER_NAME,
  SELLER_EMAIL,
  SELLER_PHONE,
  TARGET_CLOSING_DATE,
  PAL_NEEDED_BY_DATE,
  OCP_ID,
  STATUS,
  OWNER,
  OWNER_ID,
  DIFFERENT_PERSON_CONTACT,
  PROOF_FILE
 } from "../../consts";
import { useEffect, useMemo, useRef, useState } from "react";
import { formValues, schema } from "./validations";
import { getRequiredFields } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { setPendingBillData } from "@/redux/slices";
import moment from "moment";

const useStep1 = ({onBaseSubmit}: {onBaseSubmit: any}) => {
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const ocpData = useSelector((state: RootState) => state.currentData.ocpData);
  const [keys, setKeys] = useState({
    property: Math.random() * 2,
    proof: Math.random() * 2
  });
  const dispatch = useDispatch();
  const differentPersonContactRef = useRef({
    [CLOSING_CONTACT_NAME]: "",
    [CLOSING_CONTACT_EMAIL]: "",
    [CLOSING_CONTACT_PHONE]: "",
  });

  const form = useForm<formValues>({
    defaultValues: {
      [COMMUNITY]: communityData?.communityId?.toString(),
      [PROPERTY_ID]: "",
      [OWNER]: "",
      [OWNER_ID]: "",
      [RECEIPT_DATE]: undefined,
      [RECEIPT_FROM]: "",
      [REPORTER_NAME]: "",
      [REPORTER_TITLE]: "",
      [REPORTER_EMAIL]: "",
      [REPORTER_PHONE]: "",
      [SELLER_NAME]: "",
      [SELLER_EMAIL]: "",
      [SELLER_PHONE]: "",
      [TARGET_CLOSING_DATE]: undefined,
      [PAL_NEEDED_BY_DATE]: undefined,
      [CLOSING_CONTACT_SOURCE]: DIFFERENT_PERSON_CONTACT,
      [CLOSING_CONTACT_NAME]: "",
      [CLOSING_CONTACT_EMAIL]: "",
      [CLOSING_CONTACT_PHONE]: "",
      [STATUS]: "INTAKE",
    },
    resolver: zodResolver(schema),
  });
  
  const requiredFields = getRequiredFields(schema);

  const formData = useWatch({ control: form.control });
  
  const { data: propertyList, isLoading: isPropertyLoading } =
    useGetPropertiesByCommunity({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  useEffect(() => {
    if (propertyList) {
      setKeys({...keys, property: Math.random() * 3});
    }
  }, [propertyList]);

  const closingSource = useWatch({ control: form.control, name: CLOSING_CONTACT_SOURCE});

  useEffect(() => {
    if (ocpData) {
      const data = ocpData;
      form.reset({
        ...data,
        [OCP_ID]: data?.[OCP_ID]?.toString() || "",
        [COMMUNITY]: communityData?.communityId?.toString(),
        [PROPERTY_ID]: data?.[PROPERTY_ID]?.toString() || "",
        [RECEIPT_DATE]: data?.[RECEIPT_DATE] || undefined,
        [RECEIPT_FROM]: data?.[RECEIPT_FROM]?.toString() || "",
        [REPORTER_NAME]: data?.[REPORTER_NAME]?.toString() || "",
        [REPORTER_TITLE]: data?.[REPORTER_TITLE]?.toString() || "",
        [REPORTER_EMAIL]: data?.[REPORTER_EMAIL]?.toString() || "",
        [REPORTER_PHONE]: data?.[REPORTER_PHONE]?.toString() || "",
        [SELLER_NAME]: data?.[SELLER_NAME]?.toString() || "",
        [SELLER_EMAIL]: data?.[SELLER_EMAIL]?.toString() || "",
        [SELLER_PHONE]: data?.[SELLER_PHONE]?.toString() || "",
        [TARGET_CLOSING_DATE]: data?.[TARGET_CLOSING_DATE]?.toString() || undefined,
        [PAL_NEEDED_BY_DATE]: data?.[PAL_NEEDED_BY_DATE]?.toString() || undefined,
        [CLOSING_CONTACT_SOURCE]: data?.[CLOSING_CONTACT_SOURCE]?.toString(),
        [CLOSING_CONTACT_NAME]: data?.[CLOSING_CONTACT_SOURCE] == REPORTER_CONTACT ? data?.[REPORTER_NAME]?.toString() : data?.[CLOSING_CONTACT_NAME]?.toString() || "",
        [CLOSING_CONTACT_EMAIL]: data?.[CLOSING_CONTACT_SOURCE] == REPORTER_CONTACT ? data?.[REPORTER_EMAIL]?.toString() : data?.[CLOSING_CONTACT_EMAIL]?.toString() || "",
        [CLOSING_CONTACT_PHONE]: data?.[CLOSING_CONTACT_SOURCE] == REPORTER_CONTACT ? data?.[REPORTER_PHONE]?.toString() : data?.[CLOSING_CONTACT_PHONE]?.toString() || "",
        [PROOF_FILE]: data?.communicationAttachment ? {name: data?.communicationAttachment?.originalFileName, size: data?.communicationAttachment?.fileSize} : undefined,
        [STATUS]: "INTAKE"
      });
      if (data?.[CLOSING_CONTACT_SOURCE] !== REPORTER_CONTACT) {
        differentPersonContactRef.current = {
          [CLOSING_CONTACT_NAME]: data?.[CLOSING_CONTACT_NAME]?.toString() || "",
          [CLOSING_CONTACT_EMAIL]: data?.[CLOSING_CONTACT_EMAIL]?.toString() || "",
          [CLOSING_CONTACT_PHONE]: data?.[CLOSING_CONTACT_PHONE]?.toString() || "",
        };
      }
      onChangeProperty(data?.[PROPERTY_ID]?.toString())
      setKeys({
        property: Math.random() * 2,
        proof: Math.random() * 2,
      })
    }
  }, [ocpData]);

  const isReporter = useMemo(() => closingSource == REPORTER_CONTACT ,[closingSource])

  const onClosingContactChange = (value: any) => {
    const previousSource = form.getValues(CLOSING_CONTACT_SOURCE);
    form.setValue(CLOSING_CONTACT_SOURCE, value);

    if (value === REPORTER_CONTACT) {
      if (previousSource !== REPORTER_CONTACT) {
        differentPersonContactRef.current = {
          [CLOSING_CONTACT_NAME]: form.getValues(CLOSING_CONTACT_NAME) || "",
          [CLOSING_CONTACT_EMAIL]: form.getValues(CLOSING_CONTACT_EMAIL) || "",
          [CLOSING_CONTACT_PHONE]: form.getValues(CLOSING_CONTACT_PHONE) || "",
        };
      }
      form.setValue(CLOSING_CONTACT_NAME, form.getValues(REPORTER_NAME) || "");
      form.setValue(CLOSING_CONTACT_EMAIL, form.getValues(REPORTER_EMAIL) || "");
      form.setValue(CLOSING_CONTACT_PHONE, form.getValues(REPORTER_PHONE) || "");
    } else if (previousSource === REPORTER_CONTACT) {
      form.setValue(CLOSING_CONTACT_NAME, differentPersonContactRef.current[CLOSING_CONTACT_NAME]);
      form.setValue(CLOSING_CONTACT_EMAIL, differentPersonContactRef.current[CLOSING_CONTACT_EMAIL]);
      form.setValue(CLOSING_CONTACT_PHONE, differentPersonContactRef.current[CLOSING_CONTACT_PHONE]);
    }

    form.clearErrors([CLOSING_CONTACT_NAME, CLOSING_CONTACT_EMAIL, CLOSING_CONTACT_PHONE]);
  }

  const onChangePhone = () => {
    const {errors} = form.formState;
    const isReportPhoneExist = Object.keys(errors).includes(REPORTER_PHONE);
    if (!isReportPhoneExist && formData[CLOSING_CONTACT_SOURCE] == REPORTER_CONTACT) {
      form.clearErrors(CLOSING_CONTACT_PHONE);
    }
  }

  useEffect(() => {
    const currentPropertyId = form.getValues(PROPERTY_ID);
    if (propertyList?.data && currentPropertyId) {
      onChangeProperty(currentPropertyId);
    }
  }, [propertyList]);

  const onChangeProperty = (propertyId: string) => {
    if (!propertyList?.data) return; // Guard clause if list isn't ready
    
    const propertyData = propertyList.data.find((data: any) => data?.propertyId == propertyId);
    if (propertyData) {
      form.setValue(OWNER, propertyData?.[OWNER]?.toString() || "");
      form.setValue(OWNER_ID, propertyData?.[OWNER_ID]?.toString() || "");
      dispatch(setPendingBillData({ [PROPERTY_ID]: propertyId?.toString(), [OWNER_ID]: propertyData?.[OWNER_ID]?.toString() }));
    }
  }

  const onSubmit = (data: any) => {
    if (data[CLOSING_CONTACT_SOURCE] == REPORTER_CONTACT && !data[REPORTER_EMAIL]) {
      form.setError(REPORTER_EMAIL, { message: "This is field required" });
      return;
    } 
    
    if (data[CLOSING_CONTACT_SOURCE] == DIFFERENT_PERSON_CONTACT) {
      if (!data[CLOSING_CONTACT_EMAIL]) {
        form.setError(CLOSING_CONTACT_EMAIL, { message: "This is field required" });
        return;
      }
      if (!data[CLOSING_CONTACT_NAME]) {
        form.setError(CLOSING_CONTACT_NAME, { message: "This is field required" });
        return;
      }
    }

    // Format dates cleanly to prevent timezone shifts
    if (data[RECEIPT_DATE]) {
      data[RECEIPT_DATE] = moment(data[RECEIPT_DATE]).format("YYYY-MM-DD");
    }
    if (data[TARGET_CLOSING_DATE]) {
      data[TARGET_CLOSING_DATE] = moment(data[TARGET_CLOSING_DATE]).format("YYYY-MM-DD");
    }
    if (data[PAL_NEEDED_BY_DATE]) {
      data[PAL_NEEDED_BY_DATE] = moment(data[PAL_NEEDED_BY_DATE]).format("YYYY-MM-DD");
    }
    onBaseSubmit({...data});
  }

  useEffect(() => {
    if (!isReporter) return;
    form.setValue(CLOSING_CONTACT_NAME, formData[REPORTER_NAME] || "");
    form.setValue(CLOSING_CONTACT_EMAIL, formData[REPORTER_EMAIL] || "");
    form.setValue(CLOSING_CONTACT_PHONE, formData[REPORTER_PHONE] || "");
  }, [isReporter, formData[REPORTER_NAME], formData[REPORTER_EMAIL], formData[REPORTER_PHONE]]);

  return {
    propertyList: propertyList?.data,
    isLoading: isPropertyLoading,
    formData,
    isReporter,
    requiredFields,
    form,
    keys,
    onChangePhone,
    onChangeProperty,
    onClosingContactChange,
    onSubmit
  }
}

export default useStep1;